import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { X, Wallet, Coins, ArrowRight, ArrowLeft } from "lucide-react";
import { fundInitiative } from "@/lib/api/initiatives";
import { t } from "@/lib/i18n/messages";
import { formatNumber } from "@/lib/utils/format";
import { cn } from "@/lib/utils";
import type { Locale } from "@/types/auth";
import type { Initiative } from "@/types/initiative";

interface FundInitiativeModalProps {
  initiative: Initiative;
  walletBalance: number;
  locale: Locale;
  onSuccess: () => void;
  onClose: () => void;
}

const PRESET_PERCENTS: { label: string; pct: number }[] = [
  { label: "25%", pct: 0.25 },
  { label: "50%", pct: 0.5 },
  { label: "75%", pct: 0.75 },
  { label: "100%", pct: 1 },
];

export function FundInitiativeModal({
  initiative,
  walletBalance,
  locale,
  onSuccess,
  onClose,
}: FundInitiativeModalProps) {
  const isRTL = locale === "ar";
  const inputRef = useRef<HTMLInputElement>(null);
  const modalRef = useRef<HTMLDivElement>(null);

  const [amount, setAmount] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const localizedTitle =
    locale === "ar"
      ? initiative.title_ar || initiative.title
      : initiative.title_en || initiative.title;

  const numericAmount = parseFloat(amount) || 0;
  const exceedsBalance = numericAmount > walletBalance;
  const isInvalid = numericAmount <= 0 || exceedsBalance;

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
      if (e.key === "Tab" && modalRef.current) {
        const focusable = modalRef.current.querySelectorAll<HTMLElement>(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
        );
        const first = focusable[0] ?? null;
        const last = focusable[focusable.length - 1] ?? null;
        if (e.shiftKey) {
          if (document.activeElement === first && last) { e.preventDefault(); last.focus(); }
        } else {
          if (document.activeElement === last && first) { e.preventDefault(); first.focus(); }
        }
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [onClose]);

  const handlePreset = (pct: number) => {
    if (walletBalance <= 0) return;
    setAmount((Math.floor(walletBalance * pct * 100) / 100).toString());
  };

  const handleSubmit = async () => {
    if (isInvalid) return;
    setSubmitting(true);
    setError(null);
    try {
      const response = await fundInitiative(initiative.id, { amount: numericAmount });
      if (response.success) {
        onSuccess();
      } else {
        setError(response.message || t(locale, "error"));
      }
    } catch {
      setError(t(locale, "error"));
    } finally {
      setSubmitting(false);
    }
  };

  const modal = (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />

      {/* Dialog */}
      <div
        ref={modalRef}
        role="dialog"
        aria-modal="true"
        dir={isRTL ? "rtl" : "ltr"}
        className="relative w-full max-w-lg bg-card rounded-2xl shadow-xl border border-border overflow-hidden max-h-[92vh] flex flex-col"
      >
        {/* ── Header ─────────────────────────────────────────────── */}
        <div className="flex items-center justify-between gap-3 px-6 py-4 border-b border-border">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
              <Coins className="w-4 h-4 text-primary" strokeWidth={1.8} />
            </div>
            <div>
              <h2 className="font-display font-semibold text-base text-foreground leading-tight">
                {t(locale, "fundInitiative")}
              </h2>
              <p className="text-[11px] text-foreground-secondary mt-0.5 truncate max-w-[220px]">
                {localizedTitle}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="w-8 h-8 inline-flex items-center justify-center rounded-lg border border-border hover:bg-muted transition-colors"
            aria-label={t(locale, "close")}
          >
            <X className="w-4 h-4" strokeWidth={2} />
          </button>
        </div>

        {/* ── Body ───────────────────────────────────────────────── */}
        <div className="flex-1 overflow-y-auto p-6 md:p-7 space-y-6">
          {/* Amount */}
          <Section num="01" title={isRTL ? "المبلغ" : "Amount"} hint="NC">
            <div className="flex items-center gap-2">
              <input
                ref={inputRef}
                type="number"
                min="0.01"
                step="0.01"
                dir="ltr"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder="0.00"
                aria-label={t(locale, "amount")}
                className={cn(
                  "w-full h-9 px-3 bg-transparent outline-none border rounded-lg transition-colors",
                  "text-sm text-foreground placeholder:text-foreground-secondary/40 tabular-nums",
                  exceedsBalance
                    ? "border-red-500/70 focus:border-red-500"
                    : "border-border focus:border-primary",
                )}
              />
              <span className="shrink-0 text-[12px] text-foreground-secondary">NC</span>
            </div>

            {exceedsBalance && (
              <p className="font-sans text-[10px] tracking-widest text-red-500 uppercase" role="alert">
                {isRTL ? "المبلغ يتجاوز رصيدك" : "Amount exceeds your balance"}
              </p>
            )}

            <div className="flex flex-wrap gap-2">
              {PRESET_PERCENTS.map((p) => (
                <button
                  key={p.label}
                  type="button"
                  onClick={() => handlePreset(p.pct)}
                  disabled={walletBalance <= 0}
                  className={cn(
                    "px-3.5 py-2 rounded-full text-[12px] font-bold border transition-all active:scale-[0.97]",
                    amount && Math.abs(parseFloat(amount) - Math.floor(walletBalance * p.pct * 100) / 100) < 0.01
                      ? "bg-primary text-white border-primary shadow-sm"
                      : "bg-white dark:bg-background border-neutral-200 dark:border-border text-foreground hover:border-neutral-400",
                    "disabled:opacity-40 disabled:cursor-not-allowed",
                  )}
                >
                  {p.label}
                </button>
              ))}
            </div>
          </Section>

          {/* Wallet balance */}
          <Section num="02" title={isRTL ? "الرصيد" : "Wallet"}>
            <div className="rounded-xl border border-border bg-card overflow-hidden">
              <div className="flex items-center justify-between px-5 py-3">
                <span className="inline-flex items-center gap-2 font-sans text-[10px] tracking-[0.2em] uppercase text-foreground-secondary">
                  <Wallet className="w-3.5 h-3.5" strokeWidth={1.8} />
                  {t(locale, "walletBalance")}
                </span>
                <span className="font-display font-bold text-xl tabular-nums text-foreground" aria-live="polite">
                  {formatNumber(walletBalance, locale)} NC
                </span>
              </div>
            </div>
          </Section>

          {/* Warning */}
          <div className="rounded-xl border border-amber-200 bg-amber-50 dark:bg-amber-500/10 dark:border-amber-500/30 px-4 py-3">
            <p className="text-[12px] text-amber-800 dark:text-amber-400 leading-relaxed">
              {t(locale, "fundInitiativeWarning")}
            </p>
          </div>

          {error && (
            <div className="rounded-xl border border-red-500/30 bg-red-500/5 px-4 py-3">
              <p className="font-sans text-[11px] tracking-widest text-red-500 uppercase" role="alert">
                {error}
              </p>
            </div>
          )}
        </div>

        {/* ── Footer ─────────────────────────────────────────────── */}
        <div className="border-t border-border px-6 md:px-7 py-4 flex gap-3 bg-card">
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="flex-1 h-12 rounded-full border border-border text-foreground text-[14px] font-bold hover:bg-muted transition-colors disabled:opacity-50"
          >
            {t(locale, "cancelAction")}
          </button>
          <button
            type="button"
            onClick={() => void handleSubmit()}
            disabled={isInvalid || submitting}
            className={cn(
              "flex-1 h-12 rounded-full inline-flex items-center justify-center gap-2",
              "bg-primary hover:bg-primary-dark text-white",
              "text-[14px] font-bold shadow-sm transition-all active:scale-[0.98]",
              "disabled:opacity-40 disabled:cursor-not-allowed",
            )}
          >
            {submitting ? (
              <span className="inline-flex items-center gap-2">
                <span className="w-4 h-4 rounded-full border-2 border-white/40 border-t-white animate-spin" />
                <span>{isRTL ? "جارٍ التمويل..." : "Funding..."}</span>
              </span>
            ) : (
              <>
                <span>{t(locale, "confirmFund")}</span>
                {isRTL ? (
                  <ArrowLeft className="w-4 h-4" strokeWidth={2} />
                ) : (
                  <ArrowRight className="w-4 h-4" strokeWidth={2} />
                )}
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );

  return createPortal(modal, document.body);
}

// ─── Helpers ──────────────────────────────────────────────────────────────

function Section({
  num,
  title,
  hint,
  children,
}: {
  num: string;
  title: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <section className="space-y-3">
      <div className="flex items-center justify-between">
        <span className="text-[13px] font-semibold text-primary">
          {num} · {title}
        </span>
        {hint && (
          <span className="font-sans text-[10px] tracking-widest text-foreground-secondary/70 uppercase">
            {hint}
          </span>
        )}
      </div>
      {children}
    </section>
  );
}
